import React, { type ReactElement, useMemo, useState } from 'react';
import { faPersonArrowUpFromLine } from '@fortawesome/free-solid-svg-icons';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { toInteger } from 'lodash';
import ButtonGroup from 'atom-components/button-group';
import TableActions from 'atom-components/table-actions-lite';
import TableBadgeList from 'atom-components/table-badge-list';
import TableCheckbox from 'atom-components/table-checkbox';
import ServerControlledTable from 'atom-components/tables/server-controlled-table';
import type { SortingProps } from 'atom-components/tables/types';
import { friendlyModes } from 'helpers/domain-specific/response-mode.helpers';
import AllocatedMarkerBadge from 'pages/marking/components/allocated-marker-badge';
import type { BaseOrganisation } from 'services/assist-api/entities/customers/customers';
import type { BaseMarkerAllocation } from 'services/assist-api/entities/test-instance-response/marker-allocation/types';
import AllocateResponsesModal from './allocate-responses-modal';
import AllocationTableActions from './allocation-table-actions';
import type { MarkerAllocationRow } from './types';

type Props = {
  isLoading: boolean;
  markerAllocation: BaseMarkerAllocation[];
  onRequestRefetch: () => void;
  currentOrganisation: BaseOrganisation;
} & SortingProps;

export default function MarkingAllocationTable({
  isLoading,
  markerAllocation,
  onRequestRefetch,
  currentOrganisation,
  initialSorting,
  onSort
}: Props): ReactElement {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const columnHelper = createColumnHelper<MarkerAllocationRow>();
  const columns: ColumnDef<MarkerAllocationRow>[] = [
    columnHelper.accessor('id', {
      header: ({ table }) => <TableCheckbox.HeaderInput id="responses-select-all" table={table} />,
      enableSorting: false,
      cell: ({ row }) => <TableCheckbox.RowInput id={`responses-${row.original.id}`} row={row} />,
      size: 20
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <TableActions>
          <AllocationTableActions
            responseId={row.original.responseId}
            markersCount={row.original.numberOfMarkers}
            onRequestRefetch={onRequestRefetch}
          />
        </TableActions>
      ),
      size: 20
    }),
    columnHelper.accessor('nsn', {
      header: () => 'NSN',
      size: 80
    }),
    columnHelper.accessor('responseId', {
      header: () => 'Response ID',
      cell: ({ row }) => (
        <TableCheckbox.RowLabel htmlFor={`responses-${row.original.id}`}>
          {row.original.responseId}
        </TableCheckbox.RowLabel>
      ),
      size: 50
    }),
    columnHelper.accessor((row) => row.schools.join(', '), {
      id: 'schools',
      header: () => 'Schools',
      size: 100
    }),
    columnHelper.accessor('test', {
      header: () => 'Test',
      size: 100
    }),
    columnHelper.accessor('assignment', {
      header: () => 'Assignment',
      size: 100
    }),
    columnHelper.accessor('mode', {
      header: () => 'Mode',
      cell: ({ getValue }) => `${friendlyModes(getValue())}`, // getValue will return row.mode
      size: 60
    }),
    columnHelper.accessor('numberOfMarkers', {
      header: () => 'N Markers',
      size: 60
    }),
    columnHelper.accessor('markers', {
      header: () => 'Markers',
      // Note: column sorting is disabled for the Markers column for now. From Jarrod: gets
      // complicated when there are multiple markers assigned to each response etc.
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.markers.length === 0) {
          return <span>No Markers Allocated</span>;
        }
        return (
          <TableBadgeList>
            {row.original.markers.map((marker) => (
              <li key={marker.id}>
                <AllocatedMarkerBadge marker={marker} />
              </li>
            ))}
          </TableBadgeList>
        );
      }
    }),
    columnHelper.accessor('numberOfMarks', {
      header: () => 'N Marks',
      size: 60
    })
  ];

  const data: MarkerAllocationRow[] = useMemo(
    () =>
      markerAllocation.map((allocation) => ({
        id: allocation.testInstanceResponseId,
        nsn: allocation.customerUserIdentifier,
        responseId: allocation.testInstanceResponseId,
        schools: allocation.groupNames.map((groupName) => groupName),
        test: allocation.testName,
        assignment: allocation.testInstanceName,
        mode: allocation.testInstanceResponseInputTypes,
        numberOfMarkers: allocation.markersCount,
        markers:
          allocation.markers?.map((marker) => ({
            id: marker.testInstanceResponseMarkerId,
            organisationUser: {
              id: marker.customersUsersId,
              user: {
                id: marker.userId,
                firstname: marker.userFirstname,
                lastname: marker.userLastname,
                username: marker.userUsername
              }
            }
          })) || [],
        numberOfMarks: allocation.marksCount
      })),
    [markerAllocation]
  );

  const selectedResponseIds = useMemo(
    () => Object.keys(rowSelection).map((id) => toInteger(id)),
    [rowSelection]
  );

  return (
    <>
      {isModalOpen && (
        <AllocateResponsesModal
          isOpen={isModalOpen}
          onClose={(): void => setIsModalOpen(false)}
          onRequestRefetch={onRequestRefetch}
          selectedResponseIds={selectedResponseIds}
          currentOrganisation={currentOrganisation}
        />
      )}
      <ButtonGroup>
        <ButtonGroup.Button
          onClick={(): void => setIsModalOpen(true)}
          disabled={selectedResponseIds.length === 0}
          icon={faPersonArrowUpFromLine}
        >
          Allocate Responses
        </ButtonGroup.Button>
      </ButtonGroup>
      <ServerControlledTable
        isLoading={isLoading}
        columns={columns}
        data={data}
        initialSorting={initialSorting}
        onSort={onSort}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        entity="response"
      />
    </>
  );
}
